import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, LogOut, Mail, Package, CalendarDays, Repeat, Sparkles, ExternalLink } from "lucide-react";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-blue-100 text-blue-800",
  active: "bg-emerald-100 text-emerald-800",
  complete: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-200 text-gray-600",
};

function Badge({ status }) {
  const s = (status || "pending").toLowerCase();
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${STATUS_COLOR[s] || "bg-gray-100"}`}>
      {s}
    </span>
  );
}

function EmptyRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-500 py-3">
      <Icon className="w-4 h-4" />
      No {label} yet.
    </div>
  );
}

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [history, setHistory] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/orders").then((r) => setHistory(r.data)).catch(() => setHistory({ orders: [], class_registrations: [], subscription_signups: [], event_bookings: [] }));
  }, [user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/signin" replace />;

  const handleLogout = async () => { await logout(); nav("/"); };

  const manageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        email: user.email,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-[color:var(--color-bg)] py-16" data-testid="account-page">
      <div className="container-x px-6 max-w-4xl space-y-8">
        {/* Profile */}
        <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-8">
          <div className="flex items-center gap-4 mb-6">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[color:var(--color-primary)] text-white flex items-center justify-center"><User className="w-8 h-8"/></div>
            )}
            <div className="flex-1">
              <h1 className="font-display text-3xl text-[color:var(--color-primary)]">{user.name || "Welcome"}</h1>
              <div className="text-sm text-gray-500 flex items-center gap-1 mt-1"><Mail className="w-3 h-3"/> {user.email}</div>
            </div>
            <button onClick={handleLogout} className="btn-ghost" data-testid="account-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
          {history?.has_stripe_customer && (
            <button
              onClick={manageSubscription}
              disabled={portalLoading}
              className="btn-primary disabled:opacity-50"
              data-testid="manage-billing-btn"
            >
              <ExternalLink className="w-4 h-4" /> {portalLoading ? "Opening…" : "Manage subscription & billing"}
            </button>
          )}
        </div>

        {/* History */}
        {history && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="account-history">
            {/* Orders */}
            <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-4 h-4 text-[color:var(--color-accent)]" />
                <h2 className="font-display text-xl text-[color:var(--color-primary)]">Orders</h2>
              </div>
              {history.orders.length === 0 ? <EmptyRow icon={Package} label="orders" /> : (
                <ul className="divide-y divide-[color:var(--color-border)]">
                  {history.orders.map((o) => (
                    <li key={o.id} className="py-3 flex items-start justify-between gap-3" data-testid={`acct-order-${o.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[color:var(--color-primary)] truncate">
                          {o.product_name}{o.variant_name ? ` · ${o.variant_name}` : ""}
                        </div>
                        <div className="text-xs text-gray-500">
                          Qty {o.quantity} · £{Number(o.price || 0).toFixed(2)} · {(o.created_at || "").split("T")[0]}
                        </div>
                      </div>
                      <Badge status={o.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Subscriptions */}
            <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Repeat className="w-4 h-4 text-[color:var(--color-accent)]" />
                <h2 className="font-display text-xl text-[color:var(--color-primary)]">Subscriptions</h2>
              </div>
              {history.subscription_signups.length === 0 ? (
                <>
                  <EmptyRow icon={Repeat} label="subscriptions" />
                  <Link to="/subscriptions" className="text-sm text-[color:var(--color-accent)] underline">Browse plans</Link>
                </>
              ) : (
                <ul className="divide-y divide-[color:var(--color-border)]">
                  {history.subscription_signups.map((s) => (
                    <li key={s.id} className="py-3 flex items-start justify-between gap-3" data-testid={`acct-sub-${s.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[color:var(--color-primary)]">{s.plan_name}</div>
                        <div className="text-xs text-gray-500">£{Number(s.price || 0).toFixed(2)} · starts {s.start_date}</div>
                      </div>
                      <Badge status={s.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Classes */}
            <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="w-4 h-4 text-[color:var(--color-accent)]" />
                <h2 className="font-display text-xl text-[color:var(--color-primary)]">Cooking classes</h2>
              </div>
              {history.class_registrations.length === 0 ? <EmptyRow icon={CalendarDays} label="class registrations" /> : (
                <ul className="divide-y divide-[color:var(--color-border)]">
                  {history.class_registrations.map((r) => (
                    <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`acct-class-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[color:var(--color-primary)] truncate">{r.class_title}</div>
                        <div className="text-xs text-gray-500">£{Number(r.price || 0).toFixed(2)} · {(r.created_at || "").split("T")[0]}</div>
                      </div>
                      <Badge status={r.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Events */}
            <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[color:var(--color-accent)]" />
                <h2 className="font-display text-xl text-[color:var(--color-primary)]">Event enquiries</h2>
              </div>
              {history.event_bookings.length === 0 ? <EmptyRow icon={Sparkles} label="event enquiries" /> : (
                <ul className="divide-y divide-[color:var(--color-border)]">
                  {history.event_bookings.map((b) => (
                    <li key={b.id} className="py-3 flex items-start justify-between gap-3" data-testid={`acct-event-${b.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[color:var(--color-primary)]">{b.event_date}</div>
                        <div className="text-xs text-gray-500">{b.guests} guests · {b.location}</div>
                      </div>
                      <Badge status={b.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
